import { FileText, AlertTriangle, CreditCard, Scale, Mail } from "lucide-react";

export default function TermsPage() {
  const sections = [
    {
      id: "introduction",
      title: "1. Introduction and Acceptance of Terms",
      content: `Welcome to PlacementOS. These Terms and Conditions ("Terms") constitute a legally binding agreement between you ("User," "you," or "your") and PlacementOS ("Company," "we," "us," or "our").<br/><br/>
      By accessing or using our Platform, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree to these Terms, you must discontinue use of our Platform immediately.`
    },
    {
      id: "eligibility",
      title: "2. Eligibility",
      content: `To use PlacementOS, you must:<br/><br/>
      • Be at least 18 years of age<br/>
      • Have the legal capacity to enter into binding contracts<br/>
      • Not be barred from using our services under applicable law<br/>
      • Provide accurate and complete registration information<br/><br/>
      By using our Platform, you represent and warrant that you meet all eligibility requirements.`
    },
    {
      id: "accounts",
      title: "3. User Accounts and Registration",
      content: `<strong>3.1 Account Creation:</strong> You must create an account to access certain features. You agree to provide accurate, current, and complete information during registration.<br/><br/>
      <strong>3.2 Account Security:</strong> You are responsible for maintaining the confidentiality of your account credentials. Notify us immediately of any unauthorized access.<br/><br/>
      <strong>3.3 One Account Per User:</strong> Each user may maintain only one account. Multiple accounts may result in termination.`
    },
    {
      id: "services",
      title: "4. Description of Services",
      content: `PlacementOS provides the following services:<br/><br/>
      • <strong>Resume Analysis:</strong> AI-powered evaluation and optimization recommendations<br/>
      • <strong>Skill Gap Assessment:</strong> Identification of areas for professional development<br/>
      • <strong>Learning Roadmaps:</strong> Personalized pathways for skill acquisition<br/>
      • <strong>Job Matching:</strong> Connection with relevant employment opportunities<br/>
      • <strong>Career Coaching:</strong> AI-assisted guidance and advice<br/><br/>
      Services may be updated, modified, or discontinued at our discretion.`
    },
    {
      id: "license",
      title: "5. License and Access Rights",
      content: `<strong>5.1 Limited License:</strong> We grant you a limited, non-exclusive, non-transferable, revocable license to access and use our Platform for personal, non-commercial purposes.<br/><br/>
      <strong>5.2 Restrictions:</strong> You may not:<br/>
      • Copy, modify, or distribute our content without permission<br/>
      • Use automated systems to access our Platform (except approved APIs)<br/>
      • Reverse engineer or attempt to extract source code<br/>
      • Use our services for any illegal purpose`
    },
    {
      id: "user-content",
      title: "6. User Content and Data",
      content: `<strong>6.1 Your Content:</strong> You retain ownership of content you upload (resumes, profiles, etc.). By uploading, you grant us a license to use, process, and analyze this content to provide our services.<br/><br/>
      <strong>6.2 Content Standards:</strong> You agree that your content will not:<br/>
      • Contain false or misleading information<br/>
      • Infringe on intellectual property rights<br/>
      • Contain malware or harmful code<br/>
      • Violate any applicable laws`
    },
    {
      id: "prohibited",
      title: "7. Prohibited Activities",
      content: `You agree not to:<br/><br/>
      • Use the Platform for fraudulent purposes<br/>
      • Impersonate any person or entity<br/>
      • Interfere with the Platform's operation<br/>
      • Scrape or harvest user data<br/>
      • Submit false job applications<br/>
      • Share your account credentials<br/>
      • Circumvent security measures<br/>
      • Use the Platform to spam or harass others`
    },
    {
      id: "payments",
      title: "8. Payments, Subscriptions, and Billing",
      content: `<strong>8.1 Pricing:</strong> Current pricing is displayed on our Platform. All prices are in Indian Rupees (INR) unless otherwise stated.<br/><br/>
      <strong>8.2 Payment Processing:</strong> Payments are processed through secure third-party processors. We do not store complete payment card information.<br/><br/>
      <strong>8.3 Subscriptions:</strong> Subscriptions auto-renew unless cancelled before the renewal date. You can manage subscriptions in your account settings.<br/><br/>
      <strong>8.4 Taxes:</strong> Prices may be subject to applicable taxes (GST) as required by law.`
    },
    {
      id: "refunds",
      title: "9. Cancellation and Refund Policy",
      content: `<strong>9.1 Cancellation:</strong> You may cancel your subscription at any time through your account settings. Cancellation takes effect at the end of the current billing period.<br/><br/>
      <strong>9.2 No Refunds:</strong> All purchases are final and non-refundable. We do not offer refunds for:<br/>
      • Completed resume analyses<br/>
      • Consumed AI credits<br/>
      • Subscription fees (monthly or annual)<br/>
      • Any services accessed or utilized<br/><br/>
      By making a purchase, you acknowledge and agree to this no-refund policy. For exceptional circumstances, contact support@placementos.ai.`
    },
    {
      id: "ai-processing",
      title: "10. AI Processing and Data Usage",
      content: `<strong>10.1 AI Services:</strong> Our Platform uses artificial intelligence to analyze resumes, match jobs, and provide recommendations. These are automated suggestions and should not be solely relied upon for career decisions.<br/><br/>
      <strong>10.2 Accuracy:</strong> While we strive for accuracy, AI-generated results may contain errors. We recommend human review of all recommendations.<br/><br/>
      <strong>10.3 Data Usage:</strong> Your data may be used to improve our AI models in anonymized form. See our Privacy Policy for details.`
    },
    {
      id: "disclaimers",
      title: "11. Disclaimer of Warranties",
      content: `THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND.<br/><br/>
      We do not warrant that:<br/>
      • The Platform will be uninterrupted or error-free<br/>
      • Results will be accurate or reliable<br/>
      • Any defects will be corrected<br/>
      • The Platform is free of viruses or harmful components<br/><br/>
      We do not guarantee job placement or specific career outcomes.`
    },
    {
      id: "liability",
      title: "12. Limitation of Liability",
      content: `TO THE MAXIMUM EXTENT PERMITTED BY LAW:<br/><br/>
      • We are not liable for any indirect, incidental, special, consequential, or punitive damages<br/>
      • Our total liability shall not exceed the amount paid by you in the 12 months preceding the claim<br/>
      • We are not responsible for third-party actions, employer decisions, or job outcomes<br/><br/>
      Some jurisdictions do not allow limitation of liability, so these limitations may not apply to you.`
    },
    {
      id: "indemnification",
      title: "13. Indemnification",
      content: `You agree to indemnify and hold harmless PlacementOS, its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from:<br/><br/>
      • Your use of the Platform<br/>
      • Your violation of these Terms<br/>
      • Your violation of any third-party rights<br/>
      • Your content or data`
    },
    {
      id: "termination",
      title: "14. Term and Termination",
      content: `<strong>14.1 Term:</strong> These Terms remain in effect while you use the Platform.<br/><br/>
      <strong>14.2 Termination by You:</strong> You may terminate by deleting your account at any time.<br/><br/>
      <strong>14.3 Termination by Us:</strong> We may suspend or terminate your account for:<br/>
      • Violation of these Terms<br/>
      • Fraudulent or illegal activity<br/>
      • Extended period of inactivity<br/>
      • At our discretion with reasonable notice<br/><br/>
      <strong>14.4 Effect of Termination:</strong> Upon termination, your right to use the Platform ceases. Data may be deleted per our Privacy Policy.`
    },
    {
      id: "governing-law",
      title: "15. Dispute Resolution and Governing Law",
      content: `<strong>15.1 Governing Law:</strong> These Terms are governed by the laws of India.<br/><br/>
      <strong>15.2 Jurisdiction:</strong> Courts in Bangalore, Karnataka shall have exclusive jurisdiction.<br/><br/>
      <strong>15.3 Dispute Resolution:</strong> Before filing any claim, you agree to attempt good-faith negotiation by contacting support@placementos.ai.`
    },
    {
      id: "modifications",
      title: "16. Modifications to Terms",
      content: `We reserve the right to modify these Terms at any time. Material changes will be communicated via:<br/><br/>
      • Email notification<br/>
      • In-app notification<br/>
      • Posting on our Platform<br/><br/>
      Continued use after changes become effective constitutes acceptance. If you disagree with changes, you must stop using the Platform.`
    },
    {
      id: "contact",
      title: "17. Contact Information",
      content: `For questions about these Terms:<br/><br/>
      <strong>General Inquiries:</strong> support@placementos.ai<br/>
      <strong>Legal Matters:</strong> support@placementos.ai<br/>
      <strong>Billing Issues:</strong> support@placementos.ai<br/><br/>
      PlacementOS`
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-28 pb-12 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 bg-[#FF6B35]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <FileText className="w-8 h-8 text-[#FF6B35]" />
          </div>
          <h1 className="text-4xl font-bold text-[#0A1128] mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Terms & Conditions
          </h1>
          <p className="text-gray-600">
            Last Updated: February 12, 2026
          </p>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-8 px-6 border-b border-gray-200 bg-white sticky top-[72px] z-10">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap gap-2 justify-center">
            {[
              { icon: FileText, label: "Services", href: "#services" },
              { icon: CreditCard, label: "Payments", href: "#payments" },
              { icon: AlertTriangle, label: "Disclaimers", href: "#disclaimers" },
              { icon: Scale, label: "Legal", href: "#governing-law" }
            ].map((item, i) => (
              <a
                key={i}
                href={item.href}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full text-sm text-gray-600 hover:bg-[#FF6B35]/10 hover:text-[#FF6B35] transition-all"
              >
                <item.icon className="w-4 h-4" /> {item.label}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 px-6">
        <div className="max-w-3xl mx-auto space-y-12">
          {/* Important Notice */}
          <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-800 mb-1">Important Notice</h3>
                <p className="text-amber-700 text-sm">
                  Please read these Terms carefully before using PlacementOS. By using our Platform, you agree to be bound by these Terms. If you do not agree, please do not use our services.
                </p>
              </div>
            </div>
          </div>

          {sections.map((section) => (
            <div key={section.id} id={section.id} className="scroll-mt-40">
              <h2 className="text-xl font-bold text-[#0A1128] mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {section.title}
              </h2>
              <div 
                className="text-gray-600 leading-relaxed prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: section.content }}
              />
            </div>
          ))}
        </div>
      </section>

      {/* Contact Banner */}
      <section className="py-12 px-6 bg-gray-50">
        <div className="max-w-3xl mx-auto text-center">
          <Mail className="w-10 h-10 text-[#FF6B35] mx-auto mb-4" />
          <h3 className="text-xl font-bold text-[#0A1128] mb-2">Questions about these Terms?</h3>
          <p className="text-gray-600 mb-4">Contact our Legal Team at</p>
          <a href="mailto:support@placementos.ai" className="text-[#FF6B35] font-semibold hover:underline text-lg">
            support@placementos.ai
          </a>
        </div>
      </section>
    </div>
  );
}
